import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { GridColDef, GridRowsProp } from "@mui/x-data-grid"

import Table from "@/components/table/Table"
import Icon from "@/components/icon/Icon"
import Pagination from "@/components/table/Pagination"
import { PlayerSetting } from "@/types/setting"
import { colors } from "@/components/colors"
import { usePositionRank } from "@/hooks/usePositionRank"
import ProfileAvatar from "@/components/profileAvatar/ProfileAvatar"

import "./playerTable.scss"

export default function PlayerTable({
  data,
  selectedPlayer,
  setSelectedPlayer,
  paginationModel,
  setPaginationModel,
}: {
  data: PlayerSetting[] | undefined
  selectedPlayer?: number
  setSelectedPlayer: (idfPlayer: number | undefined) => void
  paginationModel?: {
    page: number
    pageSize: number
  }
  setPaginationModel?: (model: { page: number; pageSize: number }) => void
}) {
  const { t } = useTranslation()
  const positionRank = usePositionRank()
  const [rows, setRows] = useState<GridRowsProp<PlayerSetting>>([])
  const currentPlayerRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    setRows(
      (data || []).map((player) => ({
        ...player,
      })),
    )
  }, [data])

  useEffect(() => {
    if (selectedPlayer !== undefined) {
      currentPlayerRef.current = selectedPlayer
    }
  }, [selectedPlayer])

  const columns: GridColDef<PlayerSetting>[] = [
    {
      field: "uniformNumber",
      renderHeader: () => t("jersey"),
      minWidth: 119,
    },
    {
      field: "nickname",
      renderHeader: () => t("name"),
      minWidth: 273,
      flex: 1,
      renderCell: (params) => {
        return (
          <div className="player-table-image">
            <PlayerImage image={params.row.image} />
            <p className="player-table-name">{params.row.nickname}</p>
          </div>
        )
      },
    },
    {
      field: "position",
      renderHeader: () => t("position"),
      minWidth: 102,
      sortComparator: (v1, v2) => positionRank(v2) - positionRank(v1),
    },
    {
      field: "deviceSerial",
      renderHeader: () => t("assignedDevice"),
      minWidth: 157,
      renderCell: (params) => (params.row.deviceSerial ? params.row.deviceSerial : "-"),
    },
    {
      field: "idfPlayer",
      headerName: "",
      minWidth: 48,
      renderCell: (params) => {
        const isSelected = getIsSelected(params.row.idfPlayer)

        return isSelected ? (
          <div className="player-table-icon">
            <Icon type="arrow-right" size={20} color={colors.neutral.grey[600]} />
          </div>
        ) : null
      },
      sortable: false,
    },
  ]

  const getIsSelected = useCallback(
    (idfPlayer: number) => {
      return currentPlayerRef.current === idfPlayer
    },
    [currentPlayerRef.current],
  )

  return (
    <Table
      className="PlayerTableCont"
      columns={columns}
      rows={rows}
      getRowId={(row) => row.idfPlayer}
      rowHeight={66}
      disableColumnResize
      scrollbarSize={0}
      initialState={{
        pagination: {
          paginationModel: {
            pageSize: 10,
          },
        },
        sorting: {
          sortModel: [
            {
              field: "uniformNumber",
              sort: "asc",
            },
          ],
        },
      }}
      pagination
      paginationMode="client"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      rowSelectionModel={{
        type: "include",
        ids: new Set(currentPlayerRef.current ? [currentPlayerRef.current] : []),
      }}
      onRowSelectionModelChange={(model) => {
        if (model.ids.size > 0) {
          setSelectedPlayer(Array.from(model.ids)[0] as number)
          currentPlayerRef.current = Array.from(model.ids)[0] as number
        }
      }}
      slots={{
        pagination: Pagination,
      }}
      sx={{
        "& .MuiDataGrid-row:hover": {
          backgroundColor: "inherit",
          cursor: "pointer",
        },
      }}
    />
  )
}

function PlayerImage({ image }: { image: string | null | undefined }) {
  return <ProfileAvatar imageUrl={image} size={40} />
}
